import React, { FC, KeyboardEvent, useState } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import {
  Button,
  Grid,
  FormControl,
  InputLabel,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { Loaders, Tip, ValidatedTextField, ValidatedSwitch } from 'components';
import { useNotify, useProtectedClient } from 'hooks';
import { AdminUpdateClientInput, useAdminUpdateClientMutation } from 'api';
import { INVITE_REFERRAL_LIMIT } from 'app-constants';

import { ReferralSchema } from '../../validations';
import { useAdminUpdateClient } from '../../updaters';

const useStyles = makeStyles(theme => ({
  submitContainer: {
    marginTop: theme.spacing(2),
  },
  message: {
    marginTop: theme.spacing(2),
  },
  switch: {
    position: 'relative',
    top: -theme.spacing(),
  },
  safe: {
    color: theme.palette.grey[600],
  },
  warning: {
    color: theme.custom.palette.orange,
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const Referrals: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const notify = useNotify();
  const { id, referral_headline, referral_message, allow_user_invites } = useProtectedClient();

  const [focused, setFocused] = useState(false);

  const mutationOptions = useAdminUpdateClient();
  const { mutate: adminUpdateClientMutation, isLoading } = useAdminUpdateClientMutation({
    onSuccess: () => {
      notify.info({ message: t('admin:referralSuccess') });
    },
    ...mutationOptions,
  });

  function getInitialValue(): Partial<AdminUpdateClientInput> {
    return {
      id,
      referral_headline: referral_headline || t('invites:module'),
      referral_message: referral_message || t('invites:moduleText'),
      allow_user_invites: allow_user_invites || false,
    };
  }

  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  function handleSubmit(values: AdminUpdateClientInput) {
    adminUpdateClientMutation({ data: values });
  }

  function renderMessageInput(message: string) {
    const safeLimit = INVITE_REFERRAL_LIMIT;
    const currentCount = message?.length || 0;
    const difference = safeLimit - currentCount;

    const className = classNames({
      [classes.safe]: difference >= 21,
      [classes.warning]: difference <= 20,
      [classes.error]: difference < 0,
    });

    return (
      <FormControl fullWidth>
        <InputLabel shrink focused={focused}>
          {t('common:message')}
        </InputLabel>
        <ValidatedTextField
          removeLabel
          name="referral_message"
          i18nKey="common:message"
          placeholder={t('admin:referralMessagePlaceholder')}
          TextFieldProps={{
            variant: 'outlined',
            multiline: true,
            rows: 3,
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
            className: classes.message,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="caption" className={className}>
                  {currentCount}/{safeLimit}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>
    );
  }

  return (
    <Formik
      initialValues={getInitialValue()}
      validationSchema={ReferralSchema}
      onSubmit={handleSubmit}
    >
      {({ values }) => (
        <FormikForm onKeyDown={handleKeyDown}>
          <Grid container>
            <Grid item xs={3}>
              <Typography variant="subtitle2">{t('admin:userReferrals')}</Typography>
            </Grid>
            <Grid item xs={7}>
              <Grid container direction="column" wrap="nowrap" spacing={2}>
                <Grid item>
                  <ValidatedTextField
                    persistLabel
                    name="referral_headline"
                    i18nKey="admin:headline"
                    color="primary"
                    TextFieldProps={{ margin: 'none' }}
                  />
                </Grid>
                <Grid item>{renderMessageInput(values.referral_message)}</Grid>
                <Grid item>
                  <Tip i18nKey="admin:referralTip" disableMargin />
                </Grid>
              </Grid>
            </Grid>
            <Grid container justify="center" alignItems="flex-start" item xs={2}>
              <ValidatedSwitch name="allow_user_invites" color="primary" />
            </Grid>
          </Grid>

          <Grid container justify="flex-end" className={classes.submitContainer}>
            <div className={classes.submitContainer}>
              <Button type="submit" size="large" variant="contained" color="primary" fullWidth>
                {isLoading ? <Loaders.Standard height={26} /> : t('common:save')}
              </Button>
            </div>
            <Grid item xs={2} />
          </Grid>
        </FormikForm>
      )}
    </Formik>
  );
};

export default Referrals;
